import React from 'react';
import PropTypes from 'prop-types';
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import BookmarkAddedOutlinedIcon from '@mui/icons-material/BookmarkAddedOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import { CAPITAL_GAIN_SUMMARY_STATUS, formatSnapshotDate } from '../../../../Utils/capitalGainSnapshot';

export const formatSignedAmount = (value) => {
    const n = Number(value) || 0;
    if (n < 0) return `(${beautifyNumber(Math.abs(n), 0)})`;
    return beautifyNumber(n, 0);
};

const chipSx = {
    height: 24,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'var(--xtram-font)',
    '& .MuiChip-icon': { fontSize: 16 },
};

export function CapitalGainStatusChip({ summary, t }) {
    const status = summary?.status;
    if (status === CAPITAL_GAIN_SUMMARY_STATUS.loading || !status) {
        return <CircularProgress size={16} />;
    }
    if (status === CAPITAL_GAIN_SUMMARY_STATUS.error) {
        return (
            <Tooltip title={t(summary.error) || summary.error || ''} arrow>
                <Chip
                    size="small"
                    icon={<ErrorOutlineIcon />}
                    label={t('CapitalGainStatusError') || 'שגיאה בחישוב'}
                    sx={{ ...chipSx, color: '#c62828', backgroundColor: '#fdecea' }}
                />
            </Tooltip>
        );
    }
    if (status === CAPITAL_GAIN_SUMMARY_STATUS.saved) {
        const date = formatSnapshotDate(summary.savedAt);
        const label = `${t('CapitalGainStatusSaved') || 'נשמר'}${date ? ` ${date}` : ''}`;
        return (
            <Tooltip title={summary.updatedBy || ''} arrow disableHoverListener={!summary.updatedBy}>
                <Chip
                    size="small"
                    icon={<BookmarkAddedOutlinedIcon />}
                    label={label}
                    sx={{ ...chipSx, color: '#1b7a3d', backgroundColor: '#e7f6ec', '& .MuiChip-icon': { ...chipSx['& .MuiChip-icon'], color: '#1b7a3d' } }}
                />
            </Tooltip>
        );
    }
    return (
        <Chip
            size="small"
            variant="outlined"
            icon={<CalculateOutlinedIcon />}
            label={t('CapitalGainStatusComputed') || 'מחושב'}
            sx={{ ...chipSx, color: 'var(--xtram-blue, #304fff)', borderColor: 'rgba(48, 79, 255, 0.4)', '& .MuiChip-icon': { ...chipSx['& .MuiChip-icon'], color: 'var(--xtram-blue, #304fff)' } }}
        />
    );
}

CapitalGainStatusChip.propTypes = {
    summary: PropTypes.object,
    t: PropTypes.func.isRequired,
};

export function CapitalGainAmount({ summary, field }) {
    const status = summary?.status;
    if (status === CAPITAL_GAIN_SUMMARY_STATUS.loading || !status) {
        return <CircularProgress size={14} />;
    }
    if (status === CAPITAL_GAIN_SUMMARY_STATUS.error) return '—';
    return <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{formatSignedAmount(summary[field])}</span>;
}

CapitalGainAmount.propTypes = {
    summary: PropTypes.object,
    field: PropTypes.oneOf(['totalCapitalGain', 'gainBeforeTax', 'totalTax', 'balanceDue', 'salePrice']).isRequired,
};
